import { z } from 'zod';

/**
 * F17 — Cancellation policy + refund-tier vocabulary.
 *
 * A policy is an ordered list of refund tiers keyed on how many full hours
 * before slot start the cancellation occurred. At cancellation time the
 * service finds the FIRST tier where `hoursBefore >= tier.hoursBefore`
 * (tiers MUST be sorted strictly descending by `hoursBefore`) and applies
 * its `refundPercent` to the booking's snapshotted `price_amount`.
 *
 * Source of truth for refund amounts is `CancellationPolicyService.computeRefund`
 * (a pure function) — never duplicate the tier-matching logic in UI code.
 * The cancel Dialog and the server-side recorder both call the same
 * function so the percent the player sees matches what gets persisted.
 *
 * F17 records refund ELIGIBILITY (`refund_percent`, `refund_amount`,
 * `refund_eligible_at` on the bookings row); F19/F20 SETTLE the actual
 * money movement and only then flip `payment_status` to `'refunded'`.
 *
 * @see plans/17_F17_booking_lifecycle.md — Decision 2 + 3
 */

export const CancellationPolicyTierSchema = z.object({
  /**
   * Lower-bound (inclusive) on the number of hours between cancellation
   * and slot start that activates this tier. 0 means "any cancellation
   * before the slot starts" — the last tier MUST be 0 so every
   * cancellation hits some tier (typically 0% refund). 8760 = 1 year is
   * the upper bound for sanity; longer windows are not useful.
   */
  hoursBefore: z.number().int().min(0).max(8760),
  /** Percent of the booking's snapshotted price refunded at this tier. */
  refundPercent: z.number().min(0).max(100),
});
export type CancellationPolicyTier = z.infer<typeof CancellationPolicyTierSchema>;

export const CancellationPolicySchema = z.object({
  tiers: z
    .array(CancellationPolicyTierSchema)
    .min(1)
    .max(10)
    .refine((tiers) => tiers[tiers.length - 1]?.hoursBefore === 0, {
      message: 'The lowest tier must have hoursBefore=0 so every cancellation matches a tier',
    })
    .refine(
      (tiers) => tiers.every((t, i) => i === 0 || (tiers[i - 1]?.hoursBefore ?? 0) > t.hoursBefore),
      { message: 'Tiers must be sorted strictly descending by hoursBefore' },
    ),
});
export type CancellationPolicy = z.infer<typeof CancellationPolicySchema>;

/**
 * Platform-default cancellation policy applied when a venue has not set a
 * per-venue override. Locked by founder decision in the F17 brainstorming
 * round:
 *
 *   >24 hours before slot → 100% refund eligible
 *    6–24 hours before    →  50% refund eligible
 *    <6 hours / after     →   0% refund eligible
 *
 * F17 does NOT yet support runtime mutation of this constant (the
 * `PUT /admin/cancellation-policy/default` endpoint returns 501 at MVP);
 * F38 will add a `platform_settings` table for editable defaults.
 */
export const PLATFORM_DEFAULT_CANCELLATION_POLICY: CancellationPolicy = {
  tiers: [
    { hoursBefore: 24, refundPercent: 100 },
    { hoursBefore: 6, refundPercent: 50 },
    { hoursBefore: 0, refundPercent: 0 },
  ],
};

/**
 * Result of `CancellationPolicyService.computeRefund`. Used by:
 *   - the cancel Dialog UI (preview tier before confirmation)
 *   - the cancel-by-booker / cancel-by-venue / force-cancel service paths
 *     (persists `refund_percent`, `refund_amount`, `refund_eligible_at`)
 *
 * `tierApplied` is the matched tier's index inside the policy's `tiers`
 * array so the UI can highlight the matched row. `hoursBefore` is the
 * computed value at cancellation time (server clock for the persisted
 * record; client clock for the Dialog preview — they may differ by a few
 * seconds but the matched tier will be identical in 99.9% of cases).
 */
export type RefundComputation = {
  readonly refundPercent: number;
  readonly refundAmount: number;
  readonly tierApplied: number;
  readonly hoursBefore: number;
};

/**
 * Source identifier for the active policy returned by
 * `CancellationPolicyService.resolveActivePolicy`. Surfaced in the cancel
 * Dialog as "Platform default applied" vs "Custom venue policy applied"
 * microcopy.
 */
export type CancellationPolicySource = 'platform_default' | 'venue_override';

export type ActiveCancellationPolicy = {
  readonly policy: CancellationPolicy;
  readonly source: CancellationPolicySource;
};
